import {
  Button,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import FilesUploadModal, {
  FilesUploadModalFormValuesProps,
} from 'src/components/files-upload-modal';
import Iconify from 'src/components/iconify';
import ProfileFilesTable from 'src/sections/@dashboard/profile/components/ProfileFilesTable';
import ProfileFilesTableSkeleton from 'src/sections/@dashboard/profile/components/ProfileFilesTableSkeleton';
import { translateKey } from 'src/locales/translate';
import PatientNoteModal from 'src/sections/@dashboard/profile/components/PatientNoteModal/PatientNoteModal';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { useDocumentsScope } from 'src/sections/@dashboard/visit/hooks/useDocumentsScope';
import { useVisitScope } from 'src/sections/@dashboard/visit/hooks/useVisitScope';
import { CARD_HEADER_SX, CARD_HEADER_TITLE_PROPS } from 'src/theme/cardHeader';
import { useTable } from 'src/components/table';
import { TAB_TABLE_DEFAULT_ROWS_PER_PAGE } from 'src/theme/tableContainer';

export const OrganizationPatientFilesContent = () => {
  const theme = useTheme();
  const { isProfessionalRoleSelected } = useOrganizationContext();
  const { profileId: patientId, visitId } = useVisitScope();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({
    defaultRowsPerPage: TAB_TABLE_DEFAULT_ROWS_PER_PAGE,
  });

  const SUCCESS_KEYS = {
    create: 'hardcoded.pliki_zostaly_dodane',
    update: 'hardcoded.notatka_zostala_zaktualizowana',
    delete: 'hardcoded.dokument_zostal_usuniety',
    note: 'hardcoded.notatka_zostala_dodana',
  } as const;

  const ERROR_KEYS = {
    create: 'hardcoded.wystapil_blad_podczas_dodawania_plikow',
    update: 'hardcoded.wystapil_blad_podczas_zapisywania_notatki',
    delete: 'hardcoded.wystapil_blad_podczas_usuwania_dokumentu',
    note: 'hardcoded.wystapil_blad_podczas_zapisywania_notatki',
  } as const;

  const {
    items: documents,
    total,
    isLoading,
    isVisitScoped,
    upload: uploadDocuments,
    remove: deleteDocument,
    createNote,
    updateNote,
    isUploading: isUploadLoading,
    isDeleting,
    isSavingNote,
  } = useDocumentsScope({
    page,
    rowsPerPage,
    onMutationSuccess: (kind) => {
      enqueueSnackbar(translateKey(SUCCESS_KEYS[kind]), { variant: 'success' });
      if (kind === 'create') closeModal();
      if (kind === 'note') closeNoteModal();
    },
    onMutationError: (kind, error: any) =>
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          translateKey(ERROR_KEYS[kind]),
        { variant: 'error' },
      ),
  });

  const openModal = () => setIsModalOpen(true);

  const closeModal = () => setIsModalOpen(false);

  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);
  const [noteModal, setNoteModal] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    filename?: string;
    content: string;
  }>({ open: false, mode: 'create', content: '' });

  const openAddMenu = (e: React.MouseEvent<HTMLElement>) =>
    setAddMenuAnchor(e.currentTarget);
  const closeAddMenu = () => setAddMenuAnchor(null);

  const handleAddFile = () => {
    closeAddMenu();
    openModal();
  };
  const handleAddNote = () => {
    closeAddMenu();
    setNoteModal({ open: true, mode: 'create', content: '' });
  };
  const openEditNote = (note: { filename: string; content: string }) =>
    setNoteModal({
      open: true,
      mode: 'edit',
      filename: note.filename,
      content: note.content,
    });
  const closeNoteModal = () => setNoteModal((s) => ({ ...s, open: false }));

  const handleNoteSubmit = async (content: string) => {
    if (noteModal.mode === 'edit' && noteModal.filename) {
      await updateNote(noteModal.filename, content);
    } else {
      await createNote(content);
    }
  };

  const handleOnUpload = async (
    files: FilesUploadModalFormValuesProps['files'],
  ) => {
    if (files) {
      await uploadDocuments(files);
    }
  };

  return !isLoading ? (
    <>
      <Stack sx={CARD_HEADER_SX}>
        <Typography {...CARD_HEADER_TITLE_PROPS}>
          {translateKey('search.book.promoDocsTitle')}
        </Typography>
        <Button
          size="small"
          startIcon={<Iconify icon="eva:plus-fill" width={16} />}
          variant="contained"
          data-testid="file-add-open"
          sx={{
            background: theme.palette.common.black,
            color: theme.palette.common.white,
          }}
          onClick={openAddMenu}
        >
          {translateKey('common.add')}
        </Button>
        <Menu
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={closeAddMenu}
        >
          <MenuItem onClick={handleAddFile} data-testid="file-add-file">
            {translateKey('hardcoded.plik')}
          </MenuItem>
          <MenuItem onClick={handleAddNote} data-testid="file-add-note">
            {translateKey('hardcoded.notatka')}
          </MenuItem>
        </Menu>
      </Stack>
      <ProfileFilesTable
        data={documents}
        page={page}
        rowsPerPage={rowsPerPage}
        total={total}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        onDelete={deleteDocument}
        isDeleting={isDeleting}
        profileId={patientId}
        onEditTextNote={openEditNote}
      />
      <FilesUploadModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onUpload={handleOnUpload}
        isLoading={isUploadLoading}
        {...(isVisitScoped ? { visitId } : { profileId: patientId })}
      />
      <PatientNoteModal
        isOpen={noteModal.open}
        onClose={closeNoteModal}
        mode={noteModal.mode}
        initialContent={noteModal.content}
        profileId={patientId}
        visitId={isVisitScoped ? visitId : undefined}
        onSubmit={handleNoteSubmit}
        isSaving={isSavingNote}
        canUseAssistant={!!isProfessionalRoleSelected}
        enableDictation={!!isProfessionalRoleSelected}
      />
    </>
  ) : (
    <>
      <Stack sx={CARD_HEADER_SX}>
        <Skeleton variant="rounded" height="24px" width="190px" />
        <Skeleton variant="rounded" height="30px" width="77px" />
      </Stack>
      <ProfileFilesTableSkeleton />
    </>
  );
};
